import React, { Component } from "react";
import { graphql, compose } from "react-apollo";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { Scrollbars } from "react-custom-scrollbars";
import moment from "moment";
import {
  CaseManagementStore,
  CommonStore,
  SelectCase,
  GetCurrentFilter,
  GetFilteredSubsCases,
  ShowNotice
} from "Store";
import {
  queryGetCasesPagination,
  queryGetCasesPendingApprovalPagination,
  mutationAssignCaseToAgent, 
  getMe
} from "Apollo";
import { workingTimeDiff } from "../../util";
import LoadingPanel from "module-common/LoadingPanel";
import Row from "./Row";
import ServiceAlert from "module-common/ServiceAlert";
import styled from "styled-components";
import { Button } from "antd/lib/radio";
// filter case, whether case should be rejected or not
function shouldReject(tabName, currentCase, currentAgentId) {
  // common filter
  // reject any case if progress type is closed
  if (currentCase.progressType !== "OPEN") return true;

  // specific tab filter
  switch (tabName) {
    case "all":
      // on all tab, we dont have case which has been assigned
      if (currentCase.assignedTo !== null) return true;
      break;
    case "my":
      // avoid unassigned case or case assigned to other
      if (
        currentCase.assignedTo === null ||
        currentCase.assignedTo.id !== currentAgentId
      )
        return true;
      break;
    case "pending":
      // case in pending tab must have lastPendingStream field
      if (currentCase.lastPendingStream === null) return true;

      // then check if this agent actually asked for approval by drilling down to each pending streams
      for (const pending of currentCase.pendingStreams) {
        const escalatedToId = pending.escalation.escalatedTo.id;
        const status = pending.escalation.status;
        if (escalatedToId === currentAgentId && status === "ON_PROGRESS")
          return false;
      }
      return true;
    default:
      return false;
  }

  // by default include this case
  return false;
}

const ListGraphql = compose(
  graphql(queryGetCasesPagination, {
    skip: props => props.isPending,
    options : {
      notifyOnNetworkStatusChange: true
    }
  }),
  graphql(queryGetCasesPendingApprovalPagination, {
    skip: props => !props.isPending
  }),
  graphql(mutationAssignCaseToAgent, {
    name: 'assignCaseToAgent'
  })
)(
  observer(
    class ListView extends Component {
      states = observable({ 
        loadingPagination: false, 
        hasReachEnd: false
      });
      state = { 
        refetching: false,
        refetchResult: { },
        refetchStatus: false 
      }
      refetch = async () => {
        this.setState({ refetching: true })
        try {
          const result = await this.props.data.refetch()
          this.setState({ refetchResult: result, refetchStatus: true })
        } catch(e) {

        } finally {
          this.setState({ refetching: false })    
        }
        
      }

      getCombinedCase = () => {
        const {
          data: { getCasesPagination, getCasesPendingApprovalPagination }
        } = this.props;
        const {
          refetchResult: refetchData
        } = this.state
        const {
          selectedTab
        } = CaseManagementStore;
        const isPendingTab = selectedTab === "pending";
        const cases = isPendingTab
          ? getCasesPendingApprovalPagination
          : getCasesPagination;
        const refetchedCases = refetchData.data ? (
          isPendingTab 
          ? refetchData.data.getCasesPendingApprovalPagination
          : refetchData.data.getCasesPagination
        ) : null
        // combine cases from query and subs
        let combined = {};
        if (cases) {
          for (let edge of cases.edges) {
            let node = edge.node;
            combined[node.id] = {...node, cursor: edge.cursor }       
          }
        }
        if(refetchedCases) {
          for (let edge of refetchedCases.edges) {
            let node = edge.node;
            combined[node.id] = node;
            combined[node.id] = {...node, cursor: edge.cursor }
          }  
        }
        return combined
      }

      preprocessData = () => {        
        const {
          selectedTab,
          selectedCase,
          allCaseSortAsc,
          myCaseSortAsc
        } = CaseManagementStore;
        const { me: { agent: { id: currentAgentId } } } = CommonStore;
        const rows = [];

        // avoid repeating check
        const isAllTab = selectedTab === "all";
        const isMyTab = selectedTab === "my";
        const isPendingTab = selectedTab === "pending";

        // pick source of query data
        const combined = this.getCombinedCase()
        Object.assign(combined, GetFilteredSubsCases());

        // render
        let anyRowSelected = false;
        for (const key in combined) {
          const row = combined[key];

          // filter unneeded case
          if (shouldReject(selectedTab, row, currentAgentId)) continue;

          // label data preparation
          const labels = [];
          for (const label of row.labels) {
            if (label.labelTemplate.isShown) {
              labels.push({
                label: label.labelTemplate.displayName,
                value: label.labelTemplate,
                color: "#" + label.labelTemplate.color
              });
            }
          }

          // sort labels by order and date
          labels.sort((a, b) => {
            if (a.value.order !== b.value.order) {
              return a.value.order > b.value.order;
            } else {
              return moment(a.value.dateCreated).isAfter(b.value.dateCreated);
            }
          });

          // decide row color according to timestamp and working hour
          let status;
          let showIndicator = selectedTab !== "all";
          const responseTimeSLA = CommonStore.responseTimeSLA;
          const serverTime = CommonStore.serverTime;
          const timestamp = moment(row.dateUpdated);
          // const diff = serverTime.diff(timestamp, "second");

          const diff = workingTimeDiff(
            timestamp,
            CommonStore.serverTime,
            CommonStore.workingStart,
            CommonStore.workingEnd,
            CommonStore.responseTimeSLA
          );

          if (row.lastStream && row.lastStream.createdBy) {
            showIndicator = false;
          } else if (diff < responseTimeSLA * 0.5) {
            status = "normal";
          } else if (diff < responseTimeSLA) {
            status = "warning";
          } else {
            status = "alert";
          }

          // if last stream has "no need" label, then row color is normal
          if (row.lastStream && row.lastStream.labels) {
            for (const label of row.lastStream.labels) {
              if (label.labelTemplate.id === CommonStore.noNeedId) {
                status = "normal";
                showIndicator = false;
                break;
              }
            }
          }

          // get summary
          let summary = "";
          if (isPendingTab && row.lastPendingStream) {
            summary = row.lastPendingStream.content;
          } else if (row.lastStream) {
            if(row.lastStream.content)
              summary = row.lastStream.content;
            else if(row.lastStream.media) {
              summary = <i>sent you a photo</i>    
            } else summary = ""
          }

          // check if row selected
          const isSelected = selectedCase && selectedCase.id === row.id;
          if (isSelected) anyRowSelected = true;

          // check if channel signature active
          const channelSignatureActive = CommonStore.channelSignatureActive;

          rows.push({
            key: row.id,
            id: row.id,
            name: row.customerChannelHandler.name,
            channelName: row.customerChannelHandler.displayName,
            channel: row.channel.name,
            summary: summary,
            avatar: row.customerChannelHandler.displayPicture,
            timestamp: timestamp,
            serverTime: serverTime,
            labels: labels,
            showIndicator: showIndicator,
            status: status,
            needApproval: row.isEscalated,
            messageTo: row.channelHandler.nickName,
            color: row.channelHandler.color,
            channelSignatureActive: channelSignatureActive,
            progressType: row.progressType,
            onClick: () => SelectCase(row),
            onAssignToMe: () => {
              this.onAssignToOther(row)
            }
          });
        }

        // sorting case
        if (isAllTab) {
          if (allCaseSortAsc) {
            rows.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          } else {
            rows.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          }
        } else if (isMyTab) {
          // my case
          if (myCaseSortAsc) {
            rows.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          } else {
            rows.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          }
        }

        // clear selected case if no row is selected
        if (!anyRowSelected) setTimeout(() => SelectCase(null), 0);
        return rows;
      };

      render() {
        const { data: { loading, error } } = this.props;
        const { refetching, refetchStatus } = this.state
        const { selectedCase, selectedTab } = CaseManagementStore;
        const loadingPagination = this.states.loadingPagination
        const isPendingTab = selectedTab === "pending";  
        if (!loadingPagination && (loading || refetching)) {
          return <LoadingPanel />;
        } else {
          const rows = this.preprocessData();
          const paging = (<PagingPanel>
            {!isPendingTab && !this.states.loadingPagination && (
              <PagingButton 
                onClick={ev => this.onEndReached()} 
                className={this.states.loadingPagination ? 'loading' : ''}
              >
                {this.states.hasReachEnd ? 'You have reached the end of the streams!' : 'Load more'}
              </PagingButton>
            )}            
            {!isPendingTab && this.states.loadingPagination && <LoadingPanel />}
          </PagingPanel>)
          const mainList = (
            <Scrollbars
              style={{height: 'calc(100% - 30px)'}}
              ref={ref => (this.scroller = ref)}
              onScrollFrame={event => {}}
            >
              {rows.map(row => {
                const isSelected = selectedCase && selectedCase.id === row.id;
                return <Row selected={isSelected} {...row} />;
              })}
            </Scrollbars>
          )
          return (
            <div style={{height: '100%'}}>
              {mainList}
              {paging}              
            </div>  
          );
        }
      }

      onAssignToOther = async (selectedCase) => {
          const me = getMe();
          try {
            const result = await this.props.assignCaseToAgent({
              variables: {
                case: {
                  id: selectedCase.id
                },
                agent: {
                  id: me.agent.id
                }
              }
            });
            ShowNotice("case-assign-me-success");
          } catch (error) {
            ShowNotice("case-assign-me-error");
          }
        };
      

      onScrollFrame = (event, shouldLoadMore) => {
        // detect if on bottom scroll
        if (event.top === 1 && shouldLoadMore) {
          this.onEndReached();
        }
      };

      onEndReached = async () => {
        const {
          data: {
            fetchMore
          }
        } = this.props;
        const { selectedTab } = CaseManagementStore;
        const combined = this.getCombinedCase()
        const caseList = Object
          .keys(combined)
          .map(key => combined[key])
          .sort((a, b) => {
            const delta = new Date(b.dateUpdated).getTime() - new Date(a.dateUpdated).getTime()
            if(delta > 0) return 1
            else if(delta < 0) return -1
            else return 0;
          });
        // make sure we are not on top most data
        if (caseList.length > 0) {
          if (!this.states.loadingPagination) {
            this.states.loadingPagination = true;
            const length = caseList.length;
            const cursor = caseList[length - 1].cursor;
            try {
              await fetchMore({
                variables: {
                  after: cursor
                },
                updateQuery: (prevResult, { fetchMoreResult }) => {

                  if (!fetchMoreResult) return prevResult;
  
                  if (selectedTab !== "pending") {
                    const result = Object.assign(
                      {},
                      prevResult.getCasesPagination,
                      {
                        edges: [
                          ...prevResult.getCasesPagination.edges,
                          ...fetchMoreResult.getCasesPagination.edges
                        ]
                      }
                    );
                    if(!fetchMoreResult.getCasesPagination.edges.length) {
                      this.states.hasReachEnd = true
                    } else {
                      this.states.hasReachEnd = false
                    }
                    return { getCasesPagination: result };
                  } else {
                    const result = Object.assign(
                      {},
                      prevResult.getCasesPendingApprovalPagination,
                      {
                        edges: [
                          ...prevResult.getCasesPendingApprovalPagination.edges,
                          ...fetchMoreResult.getCasesPendingApprovalPagination
                            .edges
                        ]
                      }
                    );
                    return { getCasesPendingApprovalPagination: result };
                  }
                }
              });    
              this.states.loadingPagination = false;                          
            } catch(e) {
              ShowNotice("server-error-retry-later");
              console.log(e, "Error on paging")
              this.states.loadingPagination = false;  
            }
            
          }
        }
      };
    }
  )
);

const PagingPanel = styled.div`
  position: relative;
  height: 30px;  
  display: flex;
  flex-direction: column;
`

const PagingButton = styled.div`
  position: relative;
  flex: none;
  align-items: center;
  height: 30px;
  padding-top: 5px;  
  cursor: pointer;
  transition: 0.3s;
  text-align: center;
  border-bottom: 1px solid ${props => props.theme.panelBorder};
  &:hover, &.loading {
    background-color: ${props => props.theme.moduleBackground};    
  }
  &.loading {
    cursor: default;  
  }
  ${props => props.color && `background-color: ${props.theme[props.color]};`};
  ${props =>
    props.selected && `background-color: ${props.theme.moduleBackground};`};
`;

export default observer(
  class List extends Component {
  
    render() {
      const { selectedTab } = CaseManagementStore;
      let list;
      const isPendingTab = selectedTab === "pending";
      if (isPendingTab) {
        list = <ListGraphql isPending={true} />;
      } else {
        const currentFilter = GetCurrentFilter()
        const isUnassigned = currentFilter.isUnassigned
        list = (
          <div style={{height: 'calc(100% - 86px)'}}>
            {!isUnassigned && <ListGraphql {...currentFilter} assigned={true} />}
            {isUnassigned &&<ListGraphql {...currentFilter} unAssigned={true} />}
          </div>
        )        
      }
      return list
    }
  }
);



// WEBPACK FOOTER //
// ./src/module-case-management/list-panel/list/ListWithStoreQuery.js